import type { NavItem, UseNavigationDeps, VendorStatusResponse } from '~/types/ui/uiTypes'
import { useQuery } from '@tanstack/vue-query'
import { useClaimPermissions, useSessionMembership } from '@/composables/session/useSession'
import { vendorKeys } from '@/queries/adminVendors/adminVendorsKeys'
import { fetchVendorStatus } from '@/queries/adminVendors/adminVendorsQueries'

export type { NavItem, VendorStatusResponse }

export function useNavigation(deps: UseNavigationDeps = {}) {
  const route = deps.route ?? useRoute()
  const onReady = deps.onReady ?? onNuxtReady
  const isHydrated = ref(false)

  onReady(() => {
    isHydrated.value = true
  })

  const needsLandingHydrationGuard = computed(() => route.path === '/')

  const { canDistribute } = useClaimPermissions()

  const getUser = deps.getUser ?? (() => useSupabaseUser().value)
  const user = computed(() => getUser())
  const isAuthenticated = computed(() => Boolean(user.value?.sub))
  const authForUi = computed(() =>
    needsLandingHydrationGuard.value && !isHydrated.value ? false : isAuthenticated.value,
  )
  const userId = computed(() => user.value?.sub ?? null)

  const { membershipQuery } = useSessionMembership({ getUser })
  // Claims stay readable after a membership ends, so any member gets the link.
  const hasMembership = computed(() => membershipQuery.data.value != null)
  const hasMembershipForUi = computed(() =>
    needsLandingHydrationGuard.value && !isHydrated.value ? false : hasMembership.value,
  )

  const vendorStatusQuery = useQuery<VendorStatusResponse>({
    queryKey: computed(() => vendorKeys.status(userId.value)),
    queryFn: fetchVendorStatus,
    enabled: computed(() => import.meta.client && authForUi.value && userId.value != null && userId.value !== ''),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  const isVendor = computed(() => vendorStatusQuery.data.value?.isVendor ?? false)
  const isVendorForUi = computed(() =>
    needsLandingHydrationGuard.value && !isHydrated.value ? false : isVendor.value,
  )
  const canDistributeForUi = computed(() =>
    needsLandingHydrationGuard.value && !isHydrated.value ? false : canDistribute.value,
  )

  const homeItem = { icon: 'home', label: 'Home', path: '/' } as const
  const membershipItem = { icon: 'dashboard', label: 'Membership', path: '/membership' } as const
  const claimsItem = { icon: 'document', label: 'Claims', path: '/claims' } as const
  const balancesItem = { icon: 'wallet', label: 'Balances', path: '/balances' } as const
  const poolItem = { icon: 'locked', label: 'Pool', path: '/pool' } as const
  const vendorsItem = { icon: 'user-multiple', label: 'Vendors', path: '/vendors' } as const
  const extraItems = computed(() => [
    ...(authForUi.value ? [balancesItem] : []),
    ...(canDistributeForUi.value ? [poolItem] : []),
    ...(isVendorForUi.value ? [vendorsItem] : []),
  ])

  const desktopNavItems = computed(() => [
    homeItem,
    membershipItem,
    ...(hasMembershipForUi.value ? [claimsItem] : []),
    ...extraItems.value,
  ])

  const baseMobileTabs = computed(() => [
    homeItem,
    membershipItem,
    ...(hasMembershipForUi.value ? [claimsItem] : []),
  ])

  const mobileTabs = computed(() => {
    if (extraItems.value.length === 1 && baseMobileTabs.value.length < 4) {
      const [extraItem] = extraItems.value
      if (extraItem != null)
        return [...baseMobileTabs.value, extraItem]
    }
    return baseMobileTabs.value
  })

  const moreItems = computed(() => {
    if (extraItems.value.length <= 1)
      return []
    return extraItems.value
  })

  return {
    desktopNavItems,
    mobileTabs,
    moreItems,
  }
}
